// Data-owning card for the Workflow Engine (WFL) runtime surface. Mirrors ComposeCard: this
// component owns ALL data-fetching/mutations for the workflow surface — WorkflowPanel is the thin
// shell (ReplyStrip + HistoryMenu header) around it, exactly like DraftPanel wraps ComposeCard.
//
// Rendered only as a side effect of a SAM tool call (get_engagement_workflow_state,
// create_engagement, execute_workflow_transition, list_pending_approvals, decide_workflow_approval)
// — there is no dedicated tenant-admin /workflow page/route. `engagementId` absent renders the
// approvals-only view (list_pending_approvals has no engagement scope).
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/hooks';
import { cn } from '@/utils/cn';
import { apiErrorMessage, getErrorDetail, getMessageCode } from '@/utils/apiError';
import { Button } from '@/components/UI/Button';
import { Spinner } from '@/components/UI/Spinner';
import { CardSection } from '@/components/UI/CardSection';
import type { Rec } from '@/components/chat/ChatPills';
import { workspaceApi } from '@/redux/internal-services/workspaceApi';
import {
  type TaskFull,
} from '@/redux/internal-services/workspaceApiExtensions';
import TaskCard from '@/components/Tasks/TaskCard';
import {
  useDecideApprovalMutation,
  useExecuteWorkflowTransitionMutation,
  useGetEngagementByIdQuery,
  useGetEngagementWorkflowStateQuery,
  useGetWorkflowCustomizationQuery,
  useListAvailableTransitionsQuery,
  useListEngagementApprovalsQuery,
  useListPendingApprovalsQuery,
  usePutEngagementOnHoldMutation,
  useResumeEngagementMutation,
  useCloseEngagementEarlyMutation,
  useUpdateEngagementDataMutation,
  usePreviewGovernanceOptionsQuery,
  type ApprovalRequestOut,
  type AvailableTransition,
} from '@/redux/internal-services/workflowEngineApi';
import { StageRail } from './StageRail';

// Maps well-known engagement data field names to the system role code used to look up eligible users.
const ROLE_LOOKUP_FIELDS: Record<string, string> = {
  project_manager_id: 'project_manager',
  pm_id: 'project_manager',
  crew_id: 'field_crew',
  sales_agent_id: 'sales_agent',
};

const RoleLookupSelect: React.FC<{ role: string; value: string; onChange: (v: string) => void }> = ({ role, value, onChange }) => {
  const { data, isLoading } = usePreviewGovernanceOptionsQuery(role);
  const options = data?.options ?? [];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
    >
      <option value="">{isLoading ? 'Loading…' : 'Select a user…'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
};

// Transition categories that end the engagement's path down this branch (cancel/reject the
// engagement) rather than moving it forward — surfaced as a red/danger action so a user doesn't
// tap it by mistake, mirroring the destructive-button convention used elsewhere in this file
// (Close Engagement Early).
const TERMINAL_CATEGORIES = new Set(['cancel', 'reject']);

const humanize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// api-service app/common/messages.py — M.WORKFLOW_TRANSITION_BLOCKED. Raised for closed-early,
// on-hold, and blocking-governance-gate rejections alike; in every case `detail` carries the
// specific reason (a gate's own failure_message, in the governance case) rather than the generic
// per-code message, so it's worth surfacing distinctly instead of folding into the generic error box.
const TRANSITION_BLOCKED_CODE = 5215;

/** Coarse relative-time string for the "last updated" line — no need for a full i18n date lib here. */
const relativeTime = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

interface Warning {
  transitionCode: string;
  gateCode: string;
  message: string;
  reason?: string;
}

export interface WorkflowCardProps {
  /** The engagement whose workflow state/transitions this card shows. Omitted = approvals-only
   *  view (SAM's list_pending_approvals has no engagement scope). */
  engagementId?: string;
  /** Bumped whenever SAM re-opens/re-points this surface at the (possibly same) engagement —
   *  forces a refetch even when nothing invalidated the RTK Query cache (e.g. changed elsewhere). */
  rev?: number;
  /** Reports a short header title (engagement title, or "Pending approvals") up to WorkflowPanel. */
  onMeta?: (meta: { title: string }) => void;
  /** Fires with the current workflow state (or null once no engagement is open) so the host can
   *  push it to SAM as `active_engagement_workflow` context. Typed as the generic `Rec` (rather
   *  than `EngagementWorkflowStateOut`) to match the emitTaskContext/emitMeetingContext pattern in
   *  useChatWorkspace. */
  onStateChange?: (state: Rec | null) => void;
  className?: string;
  /** Engagement-scoped tasks to render below the hold/close-early section. */
  tasks?: TaskFull[];
  onSelectTask?: (id: string) => void;
  /** Approvals-only view (no engagementId): opens the approval's engagement instead of deciding
   *  it inline — see approve/reject removal below. */
  onOpenEngagement?: (engagementId: string) => void;
}

const TASK_DONE = new Set(['completed', 'done']);
const TASKS_COLLAPSED_LIMIT = 4;

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

export const WorkflowCard: React.FC<WorkflowCardProps> = ({ engagementId, rev, onMeta, onStateChange, className, tasks, onOpenEngagement }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { data: engagement } = useGetEngagementByIdQuery(engagementId as string, { skip: !engagementId });
  // The Property workspace card (WorkspacePropertyDetail, in the sibling workspaceApi slice) shows
  // this engagement's status/labels denormalized onto the property — none of that RTK Query state
  // shares tags with workflowEngineApi, so every mutation below that changes engagement/workflow
  // state must also invalidate it here, or the property card keeps showing stale data after the
  // action succeeds even though this card's own queries refetch correctly.
  const refreshPropertyWorkspace = () => {
    const propertyId = engagement?.property_id;
    if (propertyId) dispatch(workspaceApi.util.invalidateTags([{ type: 'Property', id: propertyId }]));
  };
  const {
    data: state, isLoading: stateLoading, isError: stateIsError, refetch: refetchState,
  } = useGetEngagementWorkflowStateQuery(engagementId as string, { skip: !engagementId });
  const {
    data: transitions, isLoading: transitionsLoading, refetch: refetchTransitions,
  } = useListAvailableTransitionsQuery(engagementId as string, { skip: !engagementId });
  const { data: approvalsResp } = useListEngagementApprovalsQuery(engagementId as string, { skip: !engagementId });
  const { data: globalApprovalsResp, isLoading: globalApprovalsLoading } = useListPendingApprovalsQuery(undefined, { skip: !!engagementId });
  // Tenant's stage order (for the progress rail) — same structure regardless of which engagement
  // is open, so it's fetched once and cached by RTK Query rather than per-engagement.
  const { data: customization } = useGetWorkflowCustomizationQuery(undefined, { skip: !engagementId });
  const orderedStages = [...(customization?.stages ?? [])].sort((a, b) => a.display_order - b.display_order);

  // SAM re-opened/refreshed this engagement — always look again, even if nothing invalidated the
  // cache (e.g. a change made from a different session/voice turn).
  useEffect(() => {
    if (!engagementId || rev === undefined) return;
    void refetchState();
    void refetchTransitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rev]);

  // Report a short header title up to WorkflowPanel.
  useEffect(() => {
    const title = engagementId
      ? (engagement?.title || t('workflow.title', 'Workflow'))
      : t('workflow.pendingApprovals', 'Pending Approvals');
    onMeta?.({ title });
  }, [engagement?.title, engagementId, onMeta, t]);

  // Push the on-screen workflow state to SAM (active_engagement_workflow) whenever it actually
  // changes — mirrors WorkspaceTaskDetail's resync-on-change pattern (keyed by updated_on so a
  // render alone doesn't re-push). Clears on unmount (card closed / different surface shown).
  const lastSyncedOn = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!state || state.updated_on === lastSyncedOn.current) return;
    lastSyncedOn.current = state.updated_on;
    onStateChange?.(state as unknown as Rec);
  }, [state, onStateChange]);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  useEffect(() => () => onStateChangeRef.current?.(null), []);

  // ── transitions ──────────────────────────────────────────────────────────────
  const [executeTransition, { isLoading: executing }] = useExecuteWorkflowTransitionMutation();
  const [activeTransition, setActiveTransition] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<Warning | null>(null);
  const [restrictedTransition, setRestrictedTransition] = useState<{ code: string; reason: string } | null>(null);

  // ── tasks ──────────────────────────────────────────────────────────────────
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const currentUserId = useAppSelector((s) => s.sessionState.userId);
  // Only the task's owner/assignee see the row itself (title, status, due date, actions) —
  // everyone else viewing this engagement gets no per-task detail and no number at all, not even
  // a total count (a raw count/percentage needs explaining and reads as confusing without the
  // rows behind it). Just a lightweight "something's still open" pulse shown up near the status
  // badge, not under its own "Tasks" heading — disappears once every other task is done.
  // Tasks assigned to me directly, OR unassigned queue tasks that came in via my_queues=true (not just ownership).
  // Exclude queue tasks where I'm only the owner — those arrive via mine=true but I won't have the role to claim them.
  const myTasks = (tasks ?? []).filter(t =>
    t.assignee_user_id === currentUserId ||
    (!t.assignee_user_id && !!t.queue_role_id && t.owner_user_id !== currentUserId)
  );
  const otherTaskPending = (tasks ?? []).some(t =>
    !myTasks.includes(t) && !TASK_DONE.has(t.status) && t.status !== 'cancelled',
  );


  const runTransition = async (code: string, reason?: string, acknowledgedWarnings?: string[]) => {
    if (!engagementId) return;
    setExecuteError(null);
    setBlockedMessage(null);
    try {
      const result = await executeTransition({ engagementId, transitionCode: code, reason, acknowledgedWarnings }).unwrap();
      if (result.status === 'warning_required') {
        setWarning({ transitionCode: code, gateCode: result.gate_code, message: result.message, reason });
        return;
      }
      setWarning(null);
      setActiveTransition(null);
      setReasonText('');
      onStateChange?.(result.state as unknown as Rec);
      refreshPropertyWorkspace();
    } catch (e) {
      if (getMessageCode(e) === TRANSITION_BLOCKED_CODE) {
        setBlockedMessage(getErrorDetail(e) || apiErrorMessage(e, t, t('workflow.transitionFailed', 'Could not complete that transition.')));
      } else {
        setExecuteError(apiErrorMessage(e, t, t('workflow.transitionFailed', 'Could not complete that transition.')));
      }
    }
  };

  const clickTransition = (tr: AvailableTransition) => {
    if (!tr.executable) {
      setRestrictedTransition({ code: tr.transition_code, reason: tr.reason || t('workflow.actionRestricted', 'You do not have permission to perform this action.') });
      return;
    }
    setRestrictedTransition(null);
    setExecuteError(null);
    setBlockedMessage(null);
    setApprovalSuccessMessage(null);
    if (tr.requires_reason) {
      setActiveTransition(tr.transition_code);
      setReasonText('');
      return;
    }
    void runTransition(tr.transition_code);
  };

  const confirmReasoned = () => {
    if (!activeTransition || !reasonText.trim()) return;
    void runTransition(activeTransition, reasonText.trim());
  };

  const acknowledgeWarning = () => {
    if (!warning) return;
    void runTransition(warning.transitionCode, warning.reason, [warning.gateCode]);
  };

  // ── Resolve a blocked transition's stub-gate data fields ────────────────────
  // Only for resolution.action_type === 'update_engagement_data' — the only gate type with no
  // real module UI yet (see _resolution_hint in api-service). Other action_types point the user
  // at an existing real surface (Pending Approvals below, or the property's Media/Measurements/
  // Tasks tab) instead of a form.
  const [updateEngagementData, { isLoading: savingResolveData }] = useUpdateEngagementDataMutation();
  const [resolvingTransition, setResolvingTransition] = useState<string | null>(null);
  const [resolveValues, setResolveValues] = useState<Record<string, string>>({});
  const [resolveError, setResolveError] = useState<string | null>(null);

  const openResolveForm = (tr: AvailableTransition) => {
    setResolveError(null);
    setResolveValues(Object.fromEntries((tr.resolution?.fields ?? []).map((f) => [f, ''])));
    setResolvingTransition(tr.transition_code);
  };

  const submitResolveData = async () => {
    if (!engagementId || !resolvingTransition) return;
    setResolveError(null);
    const data: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(resolveValues)) {
      data[field] = field.endsWith('_uploaded') ? value === 'true' : value;
    }
    try {
      await updateEngagementData({ id: engagementId, data }).unwrap();
      setResolvingTransition(null);
      setResolveValues({});
      void refetchTransitions();
      refreshPropertyWorkspace();
    } catch (e) {
      setResolveError(apiErrorMessage(e, t, t('workflow.resolveSaveFailed', 'Could not save those details.')));
    }
  };

  // ── approvals ────────────────────────────────────────────────────────────────
  const [decideApproval, { isLoading: deciding }] = useDecideApprovalMutation();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [approvalError, setApprovalError] = useState<string | null>(null);
  // After approving, the row itself just says "Approved on ..." — nothing points the user back
  // up at the transition button that's now unblocked (the mirror image of resolveOpenApprovalHint,
  // which points a blocked transition DOWN at this section). Surfaced for a few seconds, and
  // cleared early if the user acts on it by clicking a transition.
  const [approvalSuccessMessage, setApprovalSuccessMessage] = useState<string | null>(null);
  useEffect(() => {
    if (!approvalSuccessMessage) return;
    const id = setTimeout(() => setApprovalSuccessMessage(null), 6000);
    return () => clearTimeout(id);
  }, [approvalSuccessMessage]);

  // Scoped to the CURRENT stage, and either still pending or decided-but-still-on-the-step-it-
  // resolved-to. A decision and the transition it unblocks are two separate actions (often
  // seconds apart) — resolved_status_code is stamped onto the approval by the FIRST transition
  // after it's decided (see WorkflowApprovalRequestDAO.stamp_resolved_status), so "Approved on
  // ..." stays visible through that resulting status (e.g. Proposal Approved) and disappears once
  // the engagement moves past it into any later status (e.g. Proposal Sent) — rather than
  // vanishing the instant it's decided, or lingering through the whole (much broader) stage.
  // Requests created before stage_code existed have none and are excluded here, same tolerance
  // as elsewhere for legacy records.
  const approvals: ApprovalRequestOut[] = (approvalsResp?.data ?? []).filter(
    (a) => (!engagementId || a.engagement_id === engagementId) && a.stage_code === state?.current_stage_code
      && (a.status === 'pending' || a.resolved_status_code === state?.current_status_code),
  );

  // ── On-Hold flag (G2/G3) ─────────────────────────────────────────────────────
  // Only offered when the engine reports on_hold (already flagged) or when the current
  // process has no dedicated on-hold status of its own — the backend rejects the flag for
  // Prospects/Leads (they already have prospect_on_hold/lead_on_hold as real statuses reached
  // via their own transitions), so this button simply surfaces whatever the server allows;
  // a 409 from the server on processes that already have their own mechanism is expected and
  // shown as the error text rather than pre-guessed client-side.
  const [putOnHold, { isLoading: holding }] = usePutEngagementOnHoldMutation();
  const [resumeEngagement, { isLoading: resuming }] = useResumeEngagementMutation();
  const [holdEditorOpen, setHoldEditorOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdError, setHoldError] = useState<string | null>(null);

  const submitPutOnHold = async () => {
    if (!engagementId || !holdReason.trim()) return;
    setHoldError(null);
    try {
      await putOnHold({ engagementId, reason: holdReason.trim() }).unwrap();
      setHoldEditorOpen(false);
      setHoldReason('');
      refreshPropertyWorkspace();
    } catch (e) {
      setHoldError(apiErrorMessage(e, t, t('workflow.holdFailed', 'Could not put the engagement on hold.')));
    }
  };
  const submitResume = async () => {
    if (!engagementId) return;
    setHoldError(null);
    try {
      await resumeEngagement(engagementId).unwrap();
      refreshPropertyWorkspace();
    } catch (e) {
      setHoldError(apiErrorMessage(e, t, t('workflow.resumeFailed', 'Could not resume the engagement.')));
    }
  };

  // ── Close Engagement Early (G1) ───────────────────────────────────────────────
  // Owner-only, permanent, engagement-level outcome (not a status move) — see api-service
  // CloseEngagementEarlyRequest for why. Once set, the server blocks every further
  // transition/hold/resume on this engagement.
  const [closeEarly, { isLoading: closingEarly }] = useCloseEngagementEarlyMutation();
  const [closeEarlyEditorOpen, setCloseEarlyEditorOpen] = useState(false);
  const [closureType, setClosureType] = useState<'closed_lost' | 'future_follow_up'>('closed_lost');
  const [closeReason, setCloseReason] = useState('');
  const [closeEarlyError, setCloseEarlyError] = useState<string | null>(null);

  const submitCloseEarly = async () => {
    if (!engagementId || !closeReason.trim()) return;
    setCloseEarlyError(null);
    try {
      await closeEarly({ id: engagementId, closureType, reason: closeReason.trim() }).unwrap();
      setCloseEarlyEditorOpen(false);
      refreshPropertyWorkspace();
    } catch (e) {
      setCloseEarlyError(apiErrorMessage(e, t, t('workflow.closeEarlyFailed', 'Could not close the engagement early.')));
    }
  };

  const approve = async (approvalId: string) => {
    setApprovalError(null);
    try {
      await decideApproval({ approvalId, decision: 'approved' }).unwrap();
      refreshPropertyWorkspace();
      setApprovalSuccessMessage(t('workflow.approvalRecordedProceed', 'Approved — you can now continue with the actions above.'));
    } catch (e) {
      setApprovalError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };
  const reject = async (approvalId: string) => {
    setApprovalError(null);
    try {
      await decideApproval({ approvalId, decision: 'rejected', comments: rejectComment.trim() || undefined }).unwrap();
      setRejectingId(null);
      setRejectComment('');
      refreshPropertyWorkspace();
    } catch (e) {
      setApprovalError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };

  // ── Approvals-only view (no engagementId — list_pending_approvals tool) ────
  if (!engagementId) {
    const globalApprovals = globalApprovalsResp?.data ?? [];
    return (
      <div className={cn('rounded-2xl border border-ui-border bg-ui-card shadow-sm overflow-hidden', className)}>
        <div className="px-3 py-3 space-y-2">
          {globalApprovalsLoading ? (
            <div className="flex items-center gap-2 py-2"><Spinner size="sm" /><span className="text-xs text-ui-text3">Loading approvals…</span></div>
          ) : globalApprovals.length === 0 ? (
            <p className="text-sm text-ui-text3 py-4 text-center">No pending approvals.</p>
          ) : (
            globalApprovals.map((a) => (
              <button
                key={a.approval_request_id}
                type="button"
                onClick={() => onOpenEngagement?.(a.engagement_id)}
                disabled={!onOpenEngagement}
                className="flex w-full items-center gap-2 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-left transition hover:border-brand-dark/40 hover:bg-ui-hover disabled:cursor-default disabled:hover:border-ui-border disabled:hover:bg-ui-subtle"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-ui-text">{humanize(a.approval_type)}</span>
                    <span className="text-[10px] uppercase tracking-wide text-ui-text3">{a.status}</span>
                  </div>
                  {(a.engagement_title || a.property_address) && (
                    <p className="mt-0.5 truncate text-xs text-ui-text2">
                      {[a.engagement_title, a.property_address].filter(Boolean).join(' — ')}
                    </p>
                  )}
                  {a.required_role_label && (
                    <p className="mt-0.5 text-[11px] text-ui-text3">Requires: {a.required_role_label}</p>
                  )}
                </div>
                {onOpenEngagement && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 text-ui-text3">
                    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-2xl border border-ui-border bg-ui-card shadow-sm overflow-hidden', className)}>
      {engagementId && (
        <div className="space-y-3 border-b border-ui-border px-3 py-3">
          {stateLoading && !state ? (
            <div className="flex items-center gap-2 py-1"><Spinner size="sm" /><span className="text-xs text-ui-text3">{t('workflow.loading', 'Loading workflow state…')}</span></div>
          ) : stateIsError ? (
            <p className="text-xs text-[#CF4148]">{t('workflow.stateUnavailable', 'Workflow state unavailable.')}</p>
          ) : state ? (
            <div className="space-y-2">
              {orderedStages.length > 0 && (
                <StageRail stages={orderedStages} currentStageCode={state.current_stage_code} />
              )}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-sky-50 border border-sky-200 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:bg-sky-900/20 dark:border-sky-700/40 dark:text-sky-400">
                  {state.display_labels.status}
                </span>
                {otherTaskPending && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-ui-text3">
                    <span className="relative flex h-2 w-2 flex-shrink-0">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                    </span>
                    {t('workflow.tasksInProgress', 'Tasks in progress')}
                  </span>
                )}
                {state.updated_on && (
                  <span className="ml-auto text-[11px] text-ui-text3 whitespace-nowrap">
                    {t('workflow.lastUpdated', 'Last updated {{when}}', { when: relativeTime(state.updated_on) })}
                  </span>
                )}
              </div>

              {/* G1 — permanent closed-early banner */}
              {engagement?.closed_early ? (
                <div className="rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 dark:bg-red-900/20">
                  <p className="text-xs font-medium text-[#CF4148]">
                    {t('workflow.closedEarly', 'Closed early')} — {humanize(engagement.closed_early.closure_type)}
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#CF4148]/90">{engagement.closed_early.reason}</p>
                </div>
              ) : state.on_hold ? (
                /* G2/G3 — on-hold banner with resume */
                <div className="rounded-lg border border-amber-400/40 bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-400">{t('workflow.onHold', 'On hold')}</p>
                  {state.on_hold_reason && <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-500">{state.on_hold_reason}</p>}
                  <div className="mt-2 flex justify-end">
                    <Button size="sm" loading={resuming} onClick={() => void submitResume()}>{t('workflow.resume', 'Resume')}</Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      <div className="space-y-2 px-3 py-3">
        {/* tasks — shown first, right below the hold/close-early section. The "Tasks in progress"
            pulse for tasks the viewer doesn't own lives up in the status-badge row instead — this
            section only renders at all when there's something it can actually show: either the
            engagement has zero tasks (empty state) or the viewer owns/is assigned to at least one. */}
        {engagementId && tasks !== undefined && (tasks.length === 0 || myTasks.length > 0) && (
          <div className="-mx-3 border-b border-ui-border px-3 pb-3">
          <CardSection
            title={t('workflow.tasksTitle', 'Tasks')}
            emptyText={tasks.length === 0 ? t('workflow.tasksEmpty', 'No tasks on this engagement yet — workflow transitions will drop them here.') : undefined}
          >
            {myTasks.length > 0 && (() => {
              const canCollapse = myTasks.length > TASKS_COLLAPSED_LIMIT;
              const visible = tasksExpanded || !canCollapse ? myTasks : myTasks.slice(0, TASKS_COLLAPSED_LIMIT);
              return (
                <div className="space-y-1 pt-0.5">
                  {visible.map(task => (
                    <TaskCard
                      key={task.task_id}
                      task={task}
                      compact
                      disabled={task.permission_denied}
                      lockedReason={
                        task.gate_enforced
                          ? t('workflow.gateEnforced', 'Required before advancing the engagement')
                          : task.permission_denied
                            ? t('workflow.noPermission', 'You do not have permission to complete this task')
                            : undefined
                      }
                      onStatusError={msg => setExecuteError(msg)}
                    />
                  ))}
                  {canCollapse && (
                    <button
                      type="button"
                      onClick={() => setTasksExpanded(e => !e)}
                      className="self-start text-xs font-medium text-brand-dark hover:underline px-1 py-1"
                    >
                      {tasksExpanded ? t('common.seeLess', 'See less') : t('common.seeMore', `See more (${myTasks.length - TASKS_COLLAPSED_LIMIT})`)}
                    </button>
                  )}
                </div>
              );
            })()}
          </CardSection>
          </div>
        )}

        {/* transitions — a transition blocked only because the user lacks the role/authority for
            it (permission_denied) is hidden entirely rather than shown with an error; one blocked
            by a governance gate or on-hold/closed-early state stays visible with its reason/
            resolution hint, since that's a blockage on the engagement, not the user */}
        {engagementId && (() => {
          const visibleTransitions = (transitions ?? []).filter((tr) => tr.executable || !tr.permission_denied);
          if (!transitionsLoading && visibleTransitions.length === 0) return null;
          return (
          <CardSection>
            {transitionsLoading && !transitions ? (
              <div className="flex justify-center py-3"><Spinner size="sm" /></div>
            ) : (
              <div className="space-y-2">
                {visibleTransitions.map((tr) => {
                  const isTerminal = TERMINAL_CATEGORIES.has(tr.category);
                  return (
                  <div key={tr.transition_code}>
                    <button
                      type="button"
                      onClick={() => clickTransition(tr)}
                      disabled={executing}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left text-xs font-semibold shadow-sm transition active:scale-[0.99]',
                        isTerminal
                          ? 'cursor-pointer border-[#CF4148]/40 bg-red-50 text-[#CF4148] hover:border-[#CF4148]/60 hover:bg-red-100 hover:shadow-md dark:bg-red-900/20 dark:hover:bg-red-900/30'
                          : 'cursor-pointer border-brand-dark bg-brand-dark text-white hover:brightness-110 hover:shadow-md',
                      )}
                    >
                      <span>{tr.action_label}</span>
                    </button>
                    {restrictedTransition?.code === tr.transition_code && (
                      <p className="mt-1 pl-1 text-[11px] text-amber-700 dark:text-amber-400">{restrictedTransition.reason}</p>
                    )}
                    {!tr.executable && tr.resolution && (
                      <div className="mt-1 pl-1">
                        {tr.resolution.action_type === 'update_engagement_data' && resolvingTransition !== tr.transition_code && (
                          <button type="button" className="text-[11px] text-brand-dark hover:underline" onClick={() => openResolveForm(tr)}>
                            {t('workflow.resolveAddDetails', 'Add details')}
                          </button>
                        )}
                        {tr.resolution.action_type === 'add_contact_participant' && (
                          <p className="text-[11px] text-ui-text3">
                            {t('workflow.resolveAddContactHint', 'Ask SAM to add a contact to this property.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_media' && (
                          <p className="text-[11px] text-ui-text3">
                            {t('workflow.resolveOpenMediaHint', 'Add at least one photo for this engagement from the property\'s Media tab, then try again.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_measurement' && (
                          <p className="text-[11px] text-ui-text3">
                            {t('workflow.resolveOpenMeasurementHint', 'Record a measurement for this property linked to this engagement, then try again.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_tasks' && (
                          <p className="text-[11px] text-ui-text3">
                            {t('workflow.resolveOpenTasksHint', 'Complete the outstanding task(s) for this engagement, then try again.')}
                          </p>
                        )}
                        {tr.resolution.action_type === 'open_approval' && (
                          <p className="text-[11px] text-ui-text3">
                            {tr.resolution.required_role_label
                              ? t('workflow.resolveOpenApprovalHintRole', 'Waiting on approval from {{role}} — see Pending Approvals below.', { role: tr.resolution.required_role_label })
                              : t('workflow.resolveOpenApprovalHint', 'Waiting on approval — see Pending Approvals below.')}
                          </p>
                        )}
                      </div>
                    )}
                    {resolvingTransition === tr.transition_code && (
                      <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
                        {(tr.resolution?.fields ?? []).map((field) => (
                          <div key={field}>
                            <label className="mb-0.5 block text-[10px] text-ui-text3">{humanize(field.replace(/_uploaded$/, ''))}</label>
                            {field.endsWith('_uploaded') ? (
                              <label className="flex items-center gap-1.5 text-[11px] text-ui-text">
                                <input
                                  type="checkbox"
                                  checked={resolveValues[field] === 'true'}
                                  onChange={(e) => setResolveValues((prev) => ({ ...prev, [field]: e.target.checked ? 'true' : 'false' }))}
                                />
                                {t('workflow.resolveMarkComplete', 'Mark as complete')}
                              </label>
                            ) : ROLE_LOOKUP_FIELDS[field] ? (
                              <RoleLookupSelect
                                role={ROLE_LOOKUP_FIELDS[field]}
                                value={resolveValues[field] ?? ''}
                                onChange={(v) => setResolveValues((prev) => ({ ...prev, [field]: v }))}
                              />
                            ) : (
                              <input
                                value={resolveValues[field] ?? ''}
                                onChange={(e) => setResolveValues((prev) => ({ ...prev, [field]: e.target.value }))}
                                className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
                              />
                            )}
                          </div>
                        ))}
                        {resolveError && <p className="text-[11px] text-[#CF4148]">{resolveError}</p>}
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setResolvingTransition(null); setResolveError(null); }}>
                            {t('workflow.cancel', 'Cancel')}
                          </Button>
                          <Button
                            size="sm"
                            loading={savingResolveData}
                            disabled={Object.entries(resolveValues).some(([f, v]) => !f.endsWith('_uploaded') && !v.trim())}
                            onClick={() => void submitResolveData()}
                          >
                            {t('workflow.save', 'Save')}
                          </Button>
                        </div>
                      </div>
                    )}
                    {activeTransition === tr.transition_code && (
                      <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
                        <textarea
                          value={reasonText}
                          onChange={(e) => setReasonText(e.target.value)}
                          placeholder={t('workflow.reasonPlaceholder', 'Reason (required)')}
                          rows={2}
                          className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setActiveTransition(null); setReasonText(''); }}>
                            {t('workflow.cancel', 'Cancel')}
                          </Button>
                          <Button size="sm" loading={executing} disabled={!reasonText.trim()} onClick={confirmReasoned}>
                            {t('workflow.confirmTransition', 'Confirm')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </CardSection>
          );
        })()}

        {/* Access restriction — hidden from UI; logic kept intact so SAM can still set it */}

        {/* warning-required banner */}
        {warning && (
          <div className="rounded-lg border border-amber-400/40 bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
            <p className="text-xs text-amber-800 dark:text-amber-400">{warning.message}</p>
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setWarning(null)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button size="sm" loading={executing} onClick={acknowledgeWarning}>
                {t('workflow.acknowledgeContinue', 'Acknowledge & continue')}
              </Button>
            </div>
          </div>
        )}

        {/* blocking governance gate (or closed-early/on-hold) rejection — distinct from a generic
            error since `blockedMessage` is the gate's own failure_message, not a fallback string */}
        {blockedMessage && (
          <div className="rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 dark:bg-red-900/20">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#CF4148]">
              {t('workflow.blocked', 'Blocked')}
            </p>
            <p className="mt-0.5 text-xs text-[#CF4148]">{blockedMessage}</p>
          </div>
        )}

        {/* execute error */}
        {executeError && (
          <div className="rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 text-xs text-[#CF4148] dark:bg-red-900/20">
            {executeError}
          </div>
        )}

        {/* approval-decided hint pointing back up at the transition — kept outside the
            approvals.length gate below, since approving/rejecting can immediately drop the
            decided request out of that filtered list (resolved_status_code isn't stamped until
            the transition it unblocks actually runs), which would otherwise hide this message
            in the same instant it should appear. */}
        {approvalSuccessMessage && (
          <div className="flex items-start gap-1.5 rounded-lg border border-green-400/40 bg-green-50 px-3 py-2 dark:bg-green-900/20">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <p className="text-xs font-medium text-green-700 dark:text-green-400">{approvalSuccessMessage}</p>
          </div>
        )}

        {/* pending approvals — only rendered when at least one is present */}
        {approvals.length > 0 && (
          <>
            <CardSection title={t('workflow.pendingApprovals', 'Pending Approvals')}>
              <div className="space-y-2">
                {approvals.map((a) => (
                  <div key={a.approval_request_id} className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-ui-text">{humanize(a.approval_type)}</span>
                      <span className="text-[10px] uppercase tracking-wide text-ui-text3">{a.status}</span>
                    </div>
                    {a.required_role_label && a.status === 'pending' && (
                      <p className="mt-0.5 text-[11px] text-ui-text3">
                        {t('workflow.approvalRequires', 'Requires: {{role}}', { role: a.required_role_label })}
                      </p>
                    )}
                    {a.status !== 'pending' ? (
                      <p className="mt-1 text-xs text-ui-text3">
                        {a.decided_at
                          ? (a.status === 'approved'
                              ? t('workflow.approvalDecidedApprovedOn', 'Approved on {{date}}.', { date: fmtDate(a.decided_at) })
                              : t('workflow.approvalDecidedRejectedOn', 'Rejected on {{date}}.', { date: fmtDate(a.decided_at) }))
                          : (a.status === 'approved'
                              ? t('workflow.approvalDecidedApproved', 'Approved.')
                              : t('workflow.approvalDecidedRejected', 'Rejected.'))}
                        {a.decision_comments && ` — ${a.decision_comments}`}
                      </p>
                    ) : !a.can_decide ? (
                      <p className="mt-1 text-xs text-ui-text3">
                        {t('workflow.approvalAwaitingOther', 'Awaiting decision from the assigned approver.')}
                      </p>
                    ) : rejectingId === a.approval_request_id ? (
                      <div className="mt-2 space-y-1.5">
                        <textarea
                          value={rejectComment}
                          onChange={(e) => setRejectComment(e.target.value)}
                          placeholder={t('workflow.rejectCommentPlaceholder', 'Reason (optional)')}
                          rows={2}
                          className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { setRejectingId(null); setRejectComment(''); }}>
                            {t('workflow.cancel', 'Cancel')}
                          </Button>
                          <Button variant="destructive" size="sm" loading={deciding} onClick={() => reject(a.approval_request_id)}>
                            {t('workflow.confirmReject', 'Confirm reject')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex justify-end gap-2">
                        <Button variant="outline" size="sm" loading={deciding} onClick={() => setRejectingId(a.approval_request_id)}>
                          {t('workflow.reject', 'Reject')}
                        </Button>
                        <Button size="sm" loading={deciding} onClick={() => approve(a.approval_request_id)}>
                          {t('workflow.approve', 'Approve')}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardSection>
            {approvalError && (
              <div className="rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 text-xs text-[#CF4148] dark:bg-red-900/20">
                {approvalError}
              </div>
            )}
          </>
        )}

        {/* Put on hold / Close early — always at the very bottom */}
        {engagementId && state && !engagement?.closed_early && !state.on_hold && (
          <div className="flex items-center gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => { setHoldReason(''); setHoldError(null); setHoldEditorOpen(true); }}
              className="flex items-center gap-0.5 rounded-full border border-amber-300/70 px-2 py-0.5 text-[10px] font-medium text-amber-700 transition-colors hover:bg-amber-50 dark:border-amber-600/40 dark:text-amber-400 dark:hover:bg-amber-900/20 whitespace-nowrap"
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0">
                <rect x="6" y="4" width="4" height="16" rx="1.5"/><rect x="14" y="4" width="4" height="16" rx="1.5"/>
              </svg>
              {t('workflow.putOnHold', 'Put on hold')}
            </button>
            <button
              type="button"
              onClick={() => { setCloseReason(''); setCloseEarlyError(null); setCloseEarlyEditorOpen(true); }}
              className="flex items-center gap-0.5 rounded-full border border-[#CF4148]/40 px-2 py-0.5 text-[10px] font-medium text-[#CF4148]/80 transition-colors hover:bg-red-50 dark:border-[#CF4148]/30 dark:text-[#CF4148]/70 dark:hover:bg-red-900/20 whitespace-nowrap"
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="flex-shrink-0">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              {t('workflow.closeEarly', 'Close early')}
            </button>
          </div>
        )}
        {holdEditorOpen && (
          <div className="mt-1.5 space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2.5">
            <textarea
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder={t('workflow.holdReasonPlaceholder', 'Reason (required)')}
              rows={2}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setHoldEditorOpen(false)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button size="sm" loading={holding} disabled={!holdReason.trim()} onClick={() => void submitPutOnHold()}>{t('workflow.putOnHold', 'Put on hold')}</Button>
            </div>
          </div>
        )}
        {holdError && <p className="text-[11px] text-[#CF4148]">{holdError}</p>}
        {closeEarlyEditorOpen && (
          <div className="mt-1.5 space-y-1.5 rounded-lg border border-[#CF4148]/40 bg-red-50 p-2.5 dark:bg-red-900/20">
            <p className="text-[10px] text-[#CF4148]">
              {t('workflow.closeEarlyHint', 'Owner only. Permanent — no further transitions will be possible on this engagement.')}
            </p>
            <select
              value={closureType}
              onChange={(e) => setClosureType(e.target.value as 'closed_lost' | 'future_follow_up')}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
            >
              <option value="closed_lost">{t('workflow.closedLost', 'Closed – Lost')}</option>
              <option value="future_follow_up">{t('workflow.futureFollowUp', 'Future Follow-Up')}</option>
            </select>
            <textarea
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder={t('workflow.closeReasonPlaceholder', 'Reason (required)')}
              rows={2}
              className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-xs text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
            />
            {closeEarlyError && <p className="text-[11px] text-[#CF4148]">{closeEarlyError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCloseEarlyEditorOpen(false)}>{t('workflow.cancel', 'Cancel')}</Button>
              <Button variant="destructive" size="sm" loading={closingEarly} disabled={!closeReason.trim()} onClick={() => void submitCloseEarly()}>
                {t('workflow.closeEarly', 'Close early')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowCard;
